import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { TICKET_BUCKETS, type DecisionRoundView, type RiskTier, type TatCell } from "../lib/api/types";
import { cn } from "../lib/utils";

interface FlatRow {
  id: string;
  kind: "tier" | "bucket";
  label: string;
  tier: RiskTier;
  cell: TatCell;
  expanded?: boolean;
}

const hrs = (n: number) => `${n.toFixed(1)}`;

const columnHelper = createColumnHelper<FlatRow>();

function makeColumns(toggle: (tier: RiskTier) => void) {
  return [
    columnHelper.display({
      id: "label",
      header: "Risk tier",
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === "bucket") {
          return <span className="pl-7 text-muted-foreground">{r.label}</span>;
        }
        const Icon = r.expanded ? ChevronDown : ChevronRight;
        return (
          <button
            type="button"
            onClick={() => toggle(r.tier)}
            aria-expanded={r.expanded}
            className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-primary"
          >
            <Icon className="size-4 text-muted-foreground" />
            Tier {r.label}
          </button>
        );
      },
    }),
    columnHelper.display({
      id: "approved",
      header: "Approved",
      cell: ({ row }) => hrs(row.original.cell.approvedHours),
    }),
    columnHelper.display({
      id: "funded",
      header: "Funded",
      cell: ({ row }) => hrs(row.original.cell.fundedHours),
    }),
    columnHelper.display({
      id: "rejected",
      header: "Rejected",
      cell: ({ row }) => hrs(row.original.cell.rejectedHours),
    }),
    columnHelper.display({
      id: "total",
      header: "Total",
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{hrs(row.original.cell.totalHours)}</span>
      ),
    }),
  ] as ColumnDef<FlatRow, any>[];
}

function InlineStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {value.toLocaleString()}
      </span>
    </div>
  );
}

export function TatTable({ title, view }: { title: string; view: DecisionRoundView }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (tier: RiskTier) =>
    setExpanded((prev) => ({ ...prev, [tier]: !prev[tier] }));

  const rows: FlatRow[] = view.tatByTier.flatMap((tierRow) => {
    const isOpen = Boolean(expanded[tierRow.riskTier]);
    const head: FlatRow = {
      id: tierRow.riskTier,
      kind: "tier",
      label: tierRow.riskTier,
      tier: tierRow.riskTier,
      cell: tierRow.overall,
      expanded: isOpen,
    };
    if (!isOpen) return [head];
    return [
      head,
      ...TICKET_BUCKETS.map((bucket) => ({
        id: `${tierRow.riskTier}-${bucket}`,
        kind: "bucket" as const,
        label: bucket,
        tier: tierRow.riskTier,
        cell: tierRow.byTicketSize[bucket],
      })),
    ];
  });

  const table = useReactTable({
    data: rows,
    columns: makeColumns(toggle),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>

      <div className="grid grid-cols-4 gap-3 rounded-md border border-border/70 bg-secondary/40 p-3">
        <InlineStat label="Approved" value={view.applications.approved} />
        <InlineStat label="Funded" value={view.applications.funded} />
        <InlineStat label="Rejected" value={view.applications.rejected} />
        <InlineStat label="Total" value={view.applications.total} />
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn("text-xs", header.id !== "label" && "text-right")}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={row.original.kind === "bucket" ? "bg-secondary/20" : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "text-sm",
                      cell.column.id !== "label" && "text-right tabular-nums",
                      row.original.kind === "bucket" && "text-muted-foreground",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-primary/30 bg-amber-dim/50">
              <TableCell className="text-sm font-semibold text-primary">Overall</TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {hrs(view.tatOverall.approvedHours)}
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {hrs(view.tatOverall.fundedHours)}
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {hrs(view.tatOverall.rejectedHours)}
              </TableCell>
              <TableCell className="text-right text-sm font-semibold tabular-nums text-primary">
                {hrs(view.tatOverall.totalHours)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-muted-foreground">Hours, average per decision.</p>
    </div>
  );
}
